import { historyMappingData, processStatusChipMapper, settingTypeTableOptionList, } from '@/constants/operationSetting/operationSettingHistory';
import { i18n } from '@/locales';
import { processResult, processStatus, processType, } from '@/constants/operationSetting/option';
import { addCommaBetweenTextField, formatDate, validateTextWithHypen, } from '@/utils';
import dateFormatType from '@/constants/dateFormatType';
const lc = 'views.operation_setting_history.';
// 운행설정 리뉴얼 상세이력 클래스(실수로 2 안붙임 삭제금지)
export default class OperationSettingHistory {
    _logId;
    _createdBy;
    _createdAt;
    _memo;
    _hubId;
    _storeId;
    _agencyHubId;
    _processType;
    _processedAt;
    _processResult;
    _processedBy;
    _addTime;
    _processMemo;
    _popupCount;
    _changeAddAmount;
    _changeAddRate;
    _storeDTO;
    constructor(history) {
        this._logId = history.logId;
        this._createdBy = history.createdBy;
        this._createdAt = history.createdAt;
        this._memo = history.memo;
        this._hubId = history.hubId;
        this._storeId = history.storeId;
        this._agencyHubId = history.agencyHubId;
        this._processType = history.processType;
        this._processedAt = history.processedAt;
        this._processResult = history.processResult;
        this._processedBy = history.processedBy;
        this._addTime = history.addTime;
        this._processMemo = history.processMemo;
        this._popupCount = history.popupCount;
        this._changeAddAmount = history.changeAddAmount;
        this._changeAddRate = history.changeAddRate;
        this._storeDTO = history.storeDTO;
    }
    /** 처리과정 process_status */
    get processStatusChip() {
        return processStatusChipMapper[this._processStatus];
    }
    /** 설정대상 setting_target */
    get formattedSettingTarget() {
        if (this._isTargetEveryStoreOnHub) {
            return i18n.t(`${lc}setting_target.all_stores_belonging_to_hub`);
        }
        return this._storeDTO.name;
    }
    /** 설정유형 setting_type */
    get formattedSettingType() {
        // 서버이슈: '추가할증금액 해제'시 '추가할증금액 변경' 이라는 processType이 들어옴.
        // https://barogo.slack.com/archives/C04UP4VFCKA/p1700036129773559?thread_ts=1700029503.152779&cid=C04UP4VFCKA
        if (this._processType === processType.CHANGE_IRREGULAR_SURCHARGE
            && this._changeAddAmount === 0
            && this._changeAddRate === 0) {
            // 추가할증금액 해제
            return i18n.t(`${lc}setting_type.release_irregular_surcharge`);
        }
        return settingTypeTableOptionList[this._processType].text;
    }
    /** 내용 */
    get formattedMemoContent() {
        switch (this._processType) {
            // 설정할증 적용
            case processType.APPLY_CONFIG_SURCHARGE:
                if (this._mappedProcessMemo) {
                    return this._mappedProcessMemo.text;
                }
                return validateTextWithHypen(this._processMemo);
            // 설정할증 해제
            case processType.RELEASE_CONFIG_SURCHARGE:
                return '-';
            // 추가할증금액 변경
            case processType.CHANGE_IRREGULAR_SURCHARGE: {
                // 서버이슈: '추가할증금액 해제'시 '추가할증금액 변경'과 같은 메모가 들어옴.
                // https://barogo.slack.com/archives/C04UP4VFCKA/p1700036129773559?thread_ts=1700029503.152779&cid=C04UP4VFCKA
                if (this._changeAddAmount === 0
                    && this._changeAddRate === 0) {
                    return '-';
                }
                if (this._mappedProcessMemo) {
                    return addCommaBetweenTextField([
                        this._memo,
                        this._mappedProcessMemo.text,
                    ]);
                }
                return addCommaBetweenTextField([
                    this._memo,
                    this._processMemo,
                ]);
            }
            // 지연시간 변경
            case processType.CHANGE_DELAY_TIME:
                if (this._addTime === null) {
                    return '-';
                }
                if (this._addTime === 0) {
                    // 원활
                    return i18n.t(`${lc}content.bulletin_good`);
                }
                if (this._mappedProcessMemo) {
                    return i18n.t(`${lc}content.delay_time_with_memo`, {
                        time: this._addTime,
                        memo: this._mappedProcessMemo.text,
                    });
                }
                return i18n.t(`${lc}content.delay_time`, {
                    time: this._addTime,
                });
            // 중단 적용
            case processType.PAUSE_DELIVERY: {
                // memo값에서 맵핑테이블 기준값 포함 되어있을 때 맵핑테이블 값으로 표기
                if (this._mappedMemo) {
                    return this._mappedMemo.text;
                }
                // processMemo의 값이 '기적용'일때 processMemo 표기
                if (this._processMemo === '기적용') {
                    return this._processMemo;
                }
                // 이거 도대체 무슨 기획인지 모르겠음.... 맵핑테이블에 memo가 맞춰지면 첫번째 분기로 이미 들어갔는데?
                if (this._processMemo?.includes('오토콜전송')
                    || this._processResult === processResult.P
                    || this._processResult === processResult.X) {
                    // @ts-ignore 문제있는 로직이지만 일단 디자인 업데이트 이므로 냅둠(by 태홍 20230116).
                    return this._mappedMemo.text ?? '-';
                }
                return addCommaBetweenTextField([
                    this._memo,
                    this._processMemo,
                ]);
            }
            // 중단 해제
            case processType.ON_DELIVERY:
                return '-';
            default:
                return '-';
        }
    }
    /** 처리자 */
    get formattedProcessedBy() {
        const processedBy = this._processedBy ?? this._createdBy;
        return validateTextWithHypen(processedBy);
    }
    /** 처리일시 */
    get formattedProcessedAt() {
        const processedAt = this._processedAt ?? this._createdAt;
        if (processedAt === null) {
            return '-';
        }
        return formatDate(processedAt, dateFormatType.FULL_DATE);
    }
    // https://www.figma.com/file/LC3qOpB2dbDWxR0ZiQEUl3/%5B%EA%B4%80%EC%A0%9C%5D-%EC%9A%B4%ED%96%89%EC%84%A4%EC%A0%95-%E2%98%81%EF%B8%8F?type=design&node-id=8716-430891&mode=dev
    get _processStatus() {
        const { R, P, A, O, X, } = processResult;
        switch (this._processResult) {
            case R:
                return processStatus.CHECKING;
            case P:
            case A:
                return processStatus.IN_PROCESS;
            case O:
                return processStatus.COMPLETED;
            case X:
                return processStatus.REFUSED;
            default:
                return processStatus.REFUSED;
        }
    }
    get _isTargetEveryStoreOnHub() {
        return this._hubId === this._agencyHubId && this._storeId === 'S000000';
    }
    get _mappedProcessMemo() {
        return historyMappingData.find((item) => this._processMemo?.includes(item.type));
    }
    get _mappedMemo() {
        return historyMappingData.find((item) => this._memo?.includes(item.type));
    }
}
